import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import Pane from 'components/Common/Pane'
import Expander from 'components/utils/Expander'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineDollarSignIcon from 'LuxKit/Icons/line/LineDollarSignIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { EmptyArray, groupBy, skip, sortBy, take, last, nonNullable } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import Group from 'components/utils/Group'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useContext } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowLuxPlusNonMemberPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusNonMemberPrice'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import { FLEXIBILITY_INCLUSIONS, FARE_FEATURE_STATUS, FARE_FEATURE_TYPE } from 'constants/flight'
import { getFlightLoyaltyProductType, hasNoFlexibilityFareFeatures } from 'lib/flights/flightUtils'
import clsx from 'clsx'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import NestableClickable from 'components/Common/NestableClickable'
import RadioButton from 'LuxKit/Radio/RadioButton'
import { trackClientEvent } from 'analytics/analytics'
import useInsuranceLabels from 'hooks/Insurance/useInsuranceLabels'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import FlightsLoyaltyContext from 'contexts/Flights/FlightsLoyaltyContext'
import getLoyaltyEarnKey from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKey'
import { useAppSelector } from 'hooks/reduxHooks'
import { getFlightSegmentsState } from 'selectors/flightsSelectors'
import isLoyaltyCalculationRequestWithBonus from 'loyalty/lib/pointsCalculation/isLoyaltyCalculationRequestWithBonus'
import LineLuggageSlashIcon from 'LuxKit/Icons/line/LineLuggageSlashIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import { CHARTER } from 'constants/provider'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { FormattedMessage, useIntl } from 'react-intl'
import bookingProtectionGlossaryMessages from 'i18n/bookingProtectionGlossaryMessages'

const statusToIcon = new Map<App.FareFamilyFeatureStatus, React.ReactElement>([
  // oxlint-disable-next-line react/jsx-key
  ['CHARGED', <LineDollarSignIcon colour="highlight-primary-dark" />],
  // oxlint-disable-next-line react/jsx-key
  ['INCLUDED', <LineCheckIcon colour="highlight-primary-dark" />],
  // oxlint-disable-next-line react/jsx-key
  ['NOT_OFFERED', <LineTimesIcon />],
])

const FareCard = styled(Pane)`
  height: 100%;
  position: relative;

  &.isCroFlexibleFlightFaresEnabled {
    min-width: ${rem(275)}
  }
`

const RecommendedLabel = styled(PromotionLabel)`
  position: absolute;
  right: ${rem(16)};
  top: 0;
  transform: translateY(-50%);
  margin-top: ${rem(3)};
`

const ProtectedFareRuleSpacer = styled.div`
  height: ${rem(16)};
`

const MAX_EXTRA_ITEMS = 2
// Only show "Show more" toggle if there are more than this many hidden items
const MIN_HIDDEN_ITEMS_FOR_TOGGLE = 2

const getPriceValue = (price: number) => {
  // Check for -0, in that case we should show +0. 0 === -0 returns true
  return Math.round(price) === 0 ? 0 : price
}

const FareFamilyFeatureLabel = styled(StatusLabel)`
  position: absolute;
  right: ${rem(16)};
  top: 0;
  transform: translateY(-50%);
  margin-top: ${rem(3)};
`

interface Props {
  selected: boolean
  fareFamily: App.FlightFareFamily
  features?: Array<App.FareFamilyFeature>
  relativePrice?: number
  relativePriceDivisor?: number
  onSelect?: (data: { fareFamily: App.FlightFareFamily, pointsCalculationRequests?: Array<App.LoyaltyPointsEarnCalculationRequest | undefined> }) => void
  onShowMore?: () => void
  showMore?: boolean
  onShowFareRules?: (urls: string) => void
  onShowCfmrUnforeseenEvents?: (cost: number) => void
  isReturnType?: boolean
  journey: App.JourneyV2
  departingJourney?: App.JourneyV2
  departingFareFamily?: App.FlightFareFamily
  isStandalone?: boolean
  overrideTotalCost?: number
  overrideAdultCost?: number
  luxPlusRegularCost?: number
  luxPlusRegularAdultCost?: number
  isDomestic: boolean
  isCombined?: boolean
  isCroFlexibleFlightFaresEnabled?: boolean
  /**
   * FLY-5300: aggregate (all travellers) `+/-$X` budget label override for feeder
   * bundle (Maldives charter non-MEL feeder origin). When defined, replaces the
   * standard relativePrice math on the price row regardless of `isReturnType` /
   * `isStandalone`.
   */
  feederBudgetLabel?: number
}

function FareFamilyFareOption(props: Props) {
  const {
    selected,
    fareFamily,
    relativePrice,
    relativePriceDivisor = 1,
    features = EmptyArray,
    onSelect = noop,
    onShowMore,
    showMore,
    onShowFareRules = noop,
    onShowCfmrUnforeseenEvents = noop,
    isReturnType = false,
    departingFareFamily,
    isStandalone,
    overrideTotalCost,
    overrideAdultCost,
    luxPlusRegularCost,
    luxPlusRegularAdultCost,
    isDomestic,
    isCroFlexibleFlightFaresEnabled,
    journey,
    feederBudgetLabel,
  } = props

  const intl = useIntl()
  const { protectedFlightsName } = useInsuranceLabels()

  const showLessLabel = intl.formatMessage({
    id: '2_-wo1GfttsJOv6p_CUf',
    defaultMessage: 'Show less',
    description: 'Hyperlink text to collapse extra fare features in the flights section',
  })

  const showMoreLabel = intl.formatMessage({
    id: 'Q-NkmmNfa1Y3i5CgGSY-',
    defaultMessage: 'Show more',
    description: 'Hyperlink text to expand extra fare features in the flights section',
  })

  const refundLabel = intl.formatMessage({
    id: 'HXwBVL7zaBRHC9UWoJi4',
    defaultMessage: '100% refund for covered events',
    description: 'Label for fare with embedded insurance offering full refund',
  })

  const noCheckedBaggageLabel = intl.formatMessage({
    id: 'udmejhrMMSvanvdF22jU',
    defaultMessage: 'No checked baggage',
    description: 'Label indicating no checked baggage is included in fare family option',
  })

  const viewFareRulesLabel = intl.formatMessage({
    id: 'Vilb7luUXek0FNx7NTCE',
    defaultMessage: 'View fare rules',
    description: 'Link text to view fare rules for a fare family option',
  })

  const cancellationProtectionLabel = intl.formatMessage(bookingProtectionGlossaryMessages.cancellationProtection)

  const economyCabinLabel = intl.formatMessage({
    id: 'sjBhVyeefjN8xsn2JKz6',
    defaultMessage: 'Economy',
    description: 'Flight cabin class label for economy in the flight search or booking flow',
  })

  const businessCabinLabel = intl.formatMessage({
    id: 'm1rEYIqGvFXJROBQkdJU',
    defaultMessage: 'Business',
    description: 'Flight cabin class label for business class in the flights flow',
  })

  const cabinDisplayOverrides = useMemo<Record<string, string>>(() => ({
    'economy': economyCabinLabel,
    'premium economy': economyCabinLabel,
    'economy extra': economyCabinLabel,
    'business': businessCabinLabel,
  }), [businessCabinLabel, economyCabinLabel])

  const loyaltyPrice: number = useMemo(() => {
    if (isReturnType && isStandalone) {
      return overrideTotalCost || getPriceValue(fareFamily.costWithInsurance?.all || fareFamily.cost.all)
    }

    if (!isStandalone) {
      return getPriceValue(fareFamily.costWithInsurance?.all || fareFamily.cost.all)
    }

    return getPriceValue(fareFamily.costWithInsurance?.adult || fareFamily.cost.adult)
  }, [isReturnType, isStandalone, overrideTotalCost, fareFamily])

  const cabinLabel = useMemo(() => {
    if (journey.provider !== CHARTER) {
      return fareFamily.cabin
    }

    const normalizedCabin = fareFamily.cabin.trim().toLowerCase()
    return cabinDisplayOverrides[normalizedCabin] ?? fareFamily.cabin
  }, [cabinDisplayOverrides, fareFamily.cabin, journey.provider])

  const cabinText = intl.formatMessage({
    id: 'G3DxtLdYqTo20JULWgoF',
    defaultMessage: 'Cabin: {cabinLabel}',
    description: 'Label showing the cabin class for a fare family option',
  }, { cabinLabel })

  const showLoyaltyPoints = useContext(FlightsLoyaltyContext)

  const flightSegmentsState = useAppSelector(getFlightSegmentsState)
  const loyaltyPointsEarnCalculations = useAppSelector(state => state.loyalty.pointsEarnCalculations)

  const previouslySelectedPointsCalculationRequests = useMemo((): Array<App.LoyaltyPointsEarnCalculationRequest | undefined> => {
    return flightSegmentsState.segmentsData.reduce((acc, data) => {
      if (data.loyaltyPointsKeys) {
        const pointsCalculationRequests = data.loyaltyPointsKeys.map((key: string) => loyaltyPointsEarnCalculations[key]?.data?.request)
        return [...acc, ...pointsCalculationRequests]
      }
      return acc
    }, [])
  }, [flightSegmentsState.segmentsData, loyaltyPointsEarnCalculations])

  /**
   * More info on why this is needed:
   * @see {@link loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKeyFromFlightItems.ts}
   */
  const firstPreviouslySelectedEarnKey = useMemo((): string | undefined => {
    // Skip propagation when a previously selected segment already has an earnKey
    const hasPreviousEarnKey = previouslySelectedPointsCalculationRequests.some((request) => request?.earnKey)
    if (hasPreviousEarnKey) return undefined

    const earnKeys = nonNullable(previouslySelectedPointsCalculationRequests.map((request) => {
      if (request && isLoyaltyCalculationRequestWithBonus(request) && request.earnKey) {
        return request.earnKey
      }
    }))

    return earnKeys[0]
  }, [previouslySelectedPointsCalculationRequests])

  const calculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    const type = getFlightLoyaltyProductType(fareFamily.cabin.toLowerCase(), isDomestic)
    const earnKey = firstPreviouslySelectedEarnKey || getLoyaltyEarnKey({
      loyaltyProductType: type,
      options: {
        originAirportCode: journey.flightGroup.flights[0].departureAirport,
        destinationAirportCode: last(journey.flightGroup.flights).arrivalAirport,
        carrier: journey.carrier,
        loyaltyEarnKey: fareFamily.loyaltyEarnKey,
      },
    })

    return [{
      loyaltyProductType: type,
      price: loyaltyPrice,
      earnKey,
      offerId: journey.id,
      packageId: '',
    }]
  }, [fareFamily, isDomestic, loyaltyPrice, journey, firstPreviouslySelectedEarnKey])

  const groupedFeatures = useMemo(() => {
    const sortedFeatures = sortBy(features, feature => feature.group, 'asc')
    const grouped = groupBy(sortedFeatures, val => val.category)
    const baggageData = grouped.get('baggage') || []
    const fareRulesData = grouped.get('fare_rules') || []
    const extraData = grouped.get('extra') || []

    return [
      {
        title: intl.formatMessage({
          id: 'vduDCPDTCbIUdBptEBRD',
          defaultMessage: 'Baggage',
          description: 'Section title for baggage features in fare family option',
        }),
        id: 'baggage',
        data: baggageData,
      },
      {
        title: intl.formatMessage({
          id: 'kR-4EM0QsH4kmMHBW4d8',
          defaultMessage: 'Fare rules',
          description: 'Section title for fare rules in fare family option',
        }),
        id: 'fare_rules',
        data: fareRulesData,
      },
      {
        title: intl.formatMessage({
          id: 'y1euztw6VJ3qQyJbp4bp',
          defaultMessage: 'Seats, meals and more',
          description: 'Section title for extra features in fare family option',
        }),
        id: 'extra',
        data: extraData,
      },
    ].filter(features => features.data.length > 0)
  }, [features, intl])

  const isCheckedBaggageIncluded = useMemo(() => {
    return features.some(feature => feature.type === FARE_FEATURE_TYPE.CHECKED_BAGGAGE && feature.status === FARE_FEATURE_STATUS.INCLUDED)
  }, [features])

  const onViewFareRules = useCallback(() => {
    onShowFareRules(fareFamily.fareRulesUrl)
  }, [fareFamily, onShowFareRules])

  const extraFeaturesLength = useMemo(() => {
    return groupedFeatures.find(feature => feature.id === 'extra')?.data.length ?? 0
  }, [groupedFeatures])

  const title = fareFamily.withEmbeddedInsurance ? protectedFlightsName : fareFamily.fareFamily.description

  const totalPriceLabel = intl.formatMessage({
    id: 'NYxrIVz7fqx_LYmzV0y5',
    defaultMessage: 'Total {price}',
    description: 'Total price for fare family option in the pricing or cost breakdown section',
  }, { price: <FormatCurrency value={Math.abs(overrideTotalCost ?? (fareFamily.costWithInsurance?.all || fareFamily.cost.all))} /> })

  let relativeDepartingProtectionCost: number | undefined

  if (departingFareFamily?.withEmbeddedInsurance && fareFamily.withEmbeddedInsurance && !isReturnType) {
    relativeDepartingProtectionCost = isStandalone ? (departingFareFamily.costWithInsurance?.adult ?? 0) - (departingFareFamily.cost.adult ?? 0) : (departingFareFamily.costWithInsurance?.all ?? 0) - (departingFareFamily.cost.all ?? 0)
  }

  const isAllTravellerRelativePrice = relativePrice !== undefined && !isStandalone

  const overrideMemberFareCost = isStandalone ? overrideAdultCost : overrideTotalCost

  const luxPlusMemberFareCost = overrideMemberFareCost ?? (isAllTravellerRelativePrice
    ? fareFamily.cost.all
    : fareFamily.cost.adult)

  const overrideRegularFareCost = isStandalone ? luxPlusRegularAdultCost : luxPlusRegularCost

  const luxPlusRegularFareCost = overrideRegularFareCost ?? (isAllTravellerRelativePrice
    ? fareFamily.regularCost?.all : fareFamily.regularCost?.adult)

  const fareFamilyAllTravellerDisplayCost = overrideTotalCost ?? (fareFamily.costWithInsurance?.all || fareFamily.cost.all)
  const fareFamilyAdultDisplayCost = overrideAdultCost ?? (fareFamily.costWithInsurance?.adult || fareFamily.cost.adult)

  const getDisplayedPriceDelta = useCallback((totalCost: number, protectionCost = 0) => {
    const totalDelta = totalCost - (relativePrice ?? 0) - protectionCost
    return totalDelta / relativePriceDivisor
  }, [relativePrice, relativePriceDivisor])

  const luxPlusMemberFarePrice = getDisplayedPriceDelta(luxPlusMemberFareCost)

  const luxPlusNonMemberFarePrice = getDisplayedPriceDelta(luxPlusRegularFareCost ?? 0)
  const luxPlusFareSaveAmount = luxPlusNonMemberFarePrice - luxPlusMemberFarePrice
  const showLuxPlusPrice = Boolean(
    fareFamily.bookingInfo?.isLuxPlusFare &&
    (fareFamily.regularCost || luxPlusRegularCost !== undefined) &&
    luxPlusMemberFarePrice < luxPlusNonMemberFarePrice,
  )
  const shouldShowLuxPlusSaveLabel = showLuxPlusPrice && luxPlusFareSaveAmount > 0

  const onClickInsuranceInfoIcon = (e: React.MouseEvent) => {
    e.stopPropagation()
    onShowCfmrUnforeseenEvents(fareFamily.cost.all)
  }

  const onClickCfmrUnforeseenEvents = (e: React.MouseEvent) => {
    e.stopPropagation()
    onShowCfmrUnforeseenEvents(fareFamily.cost.all)
  }

  const isProtectedFareRulesSmallest = useCallback((feature: typeof groupedFeatures[0], fareFamily: App.FlightFareFamily) => {
    if (feature.id !== 'fare_rules' || !fareFamily.withEmbeddedInsurance) return

    const baggageFeaturesLenght = groupedFeatures[0].data.length
    const fareRulesFeaturesLenght = groupedFeatures[1].data.length
    const extraFeaturesLenght = groupedFeatures[2].data.length

    const isFareRulesSmaller = fareRulesFeaturesLenght < baggageFeaturesLenght || fareRulesFeaturesLenght < extraFeaturesLenght

    return isFareRulesSmaller
  }, [groupedFeatures])

  const formatFeatureDescription = useCallback((feature: App.FareFamilyFeature) => {
    if (feature.type && FLEXIBILITY_INCLUSIONS.has(feature.type)) {
      return intl.formatMessage(
        {
          id: 'cXGc_LQLcCqiKn1rw0CQ',
          defaultMessage: '{description} *',
          description: 'Fare feature description with asterisk indicating flexibility inclusion',
        },
        { description: feature.description },
      )
    }
    return feature.description
  }, [intl])

  const handleSelect = useCallback(() => {
    if (fareFamily.withEmbeddedInsurance) {
      trackClientEvent({
        subject: 'luxury_escapes_protect_fare',
        action: 'click',
        category: 'flight_search',
        type: 'interaction',
      })
    }

    onSelect({ fareFamily, pointsCalculationRequests: calculationRequests })
  }, [onSelect, fareFamily, calculationRequests])

  useEffect(() => {
    if (fareFamily.withEmbeddedInsurance) {
      trackClientEvent({
        subject: 'luxury_escapes_protect_fare',
        action: 'impression',
        category: 'flight_search',
        type: 'nonInteraction',
      })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const recommended = fareFamily.withEmbeddedInsurance || fareFamily.styling?.highlight

  return <NestableClickable onClick={handleSelect}>
    <FareCard
      data-testid="fare-family-option"
      data-fare-total-cost={overrideTotalCost ?? (fareFamily.costWithInsurance?.all || fareFamily.cost.all)}
      data-fare-has-embedded-insurance={fareFamily.withEmbeddedInsurance}
      border={recommended ? 'highlight-primary-dark' : selected ? 'normal-contrast' : 'light-foreground'}
      borderRadius="S"
      padding="24 16"
      className={clsx({ isCroFlexibleFlightFaresEnabled })}
    >
      {fareFamily.withEmbeddedInsurance && <RecommendedLabel icon={<LineShieldCheckIcon />} variant="premium">
        {refundLabel}
      </RecommendedLabel>}
      {!fareFamily.withEmbeddedInsurance && fareFamily.styling?.highlightText &&
        <RecommendedLabel variant="premium">
          {fareFamily.styling?.highlightText}
        </RecommendedLabel>}
      {!fareFamily.withEmbeddedInsurance && !fareFamily.styling?.highlightText && !isCheckedBaggageIncluded &&
        <FareFamilyFeatureLabel startIcon={<LineLuggageSlashIcon />} variant="pending">
          {noCheckedBaggageLabel}
        </FareFamilyFeatureLabel>}
      <VerticalSpacer gap={16}>
        <VerticalSpacer gap={8}>
          <Group direction="horizontal" verticalAlign="center" gap={12}>
            <RadioButton size="large" checked={selected} onClick={handleSelect} />

            <div>
              <HeadingTextBlock variant="heading6" endIcon={fareFamily.withEmbeddedInsurance ? <LineInfoCircleIcon onClick={onClickInsuranceInfoIcon} /> : undefined}>{title}</HeadingTextBlock>
              <Caption variant="medium" colour="light-foreground-alt">
                {cabinText}
              </Caption>
            </div>
          </Group>

          <div>
            <div>
              {feederBudgetLabel !== undefined &&
                <PriceRowPrice
                  size="M"
                  price={getPriceValue(feederBudgetLabel)}
                  signDisplay="always"
                  saleUnit=""
                />}

              {feederBudgetLabel === undefined && showLuxPlusPrice && <Group direction="vertical" horizontalAlign="start" gap={4}>
                {shouldShowLuxPlusSaveLabel && <LuxPlusLabel
                  hideSaveAmountPlus
                  saveAmount={luxPlusFareSaveAmount}
                  type="member-price-offer"
                />}
                <PriceRowLuxPlusPrice
                  size="M"
                  memberPrice={getPriceValue(luxPlusMemberFarePrice)}
                  signDisplay={relativePrice !== undefined ? 'always' : undefined}
                  saleUnit={isAllTravellerRelativePrice ? '' : 'adult'}
                  logoPlacement="trailing"
                />
                <PriceRowLuxPlusNonMemberPrice
                  nonMemberPrice={getPriceValue(luxPlusNonMemberFarePrice)}
                  signDisplay={relativePrice !== undefined ? 'always' : undefined}
                />
                {relativePrice !== undefined && isReturnType && isStandalone && <PriceRowCaption>
                  {totalPriceLabel}
                </PriceRowCaption>}
              </Group>}

              {feederBudgetLabel === undefined && !showLuxPlusPrice && relativePrice !== undefined && !isReturnType && isStandalone &&
                <PriceRowPrice
                  size="M"
                  price={getPriceValue(getDisplayedPriceDelta(fareFamilyAdultDisplayCost, relativeDepartingProtectionCost ?? 0))}
                  signDisplay="always"
                  saleUnit="adult"
                />}

              {feederBudgetLabel === undefined && !showLuxPlusPrice && relativePrice !== undefined && !isReturnType && !isStandalone &&
                <PriceRowPrice
                  size="M"
                  price={getPriceValue(getDisplayedPriceDelta(fareFamilyAllTravellerDisplayCost, relativeDepartingProtectionCost ?? 0))}
                  signDisplay="always"
                  saleUnit=""
                />}

              {feederBudgetLabel === undefined && !showLuxPlusPrice && relativePrice !== undefined && isReturnType && isStandalone && <Group direction="vertical">
                <PriceRowPrice
                  size="M"
                  price={getPriceValue(getDisplayedPriceDelta(fareFamilyAdultDisplayCost))}
                  signDisplay="always"
                  saleUnit="adult"
                />
                <PriceRowCaption>
                  {totalPriceLabel}
                </PriceRowCaption>
              </Group>}
              {feederBudgetLabel === undefined && !showLuxPlusPrice && relativePrice !== undefined && isReturnType && !isStandalone && <Group direction="vertical">
                <PriceRowPrice
                  size="M"
                  price={getPriceValue(getDisplayedPriceDelta(fareFamilyAllTravellerDisplayCost))}
                  signDisplay="always"
                  saleUnit=""
                />
              </Group>}
              {feederBudgetLabel === undefined && !showLuxPlusPrice && relativePrice === undefined && <PriceRowPrice
                size="S"
                price={getPriceValue(fareFamily.costWithInsurance?.adult || fareFamily.cost.adult)}
                saleUnit="adult"
              />}
            </div>
            <TextLink size="caption" onClick={onViewFareRules}>
              {viewFareRulesLabel}
            </TextLink>
          </div>
        </VerticalSpacer>
        <Divider kind="primary" />
        <VerticalSpacer gap={12}>
          <PriceRowAgentHubCommission
            offer={{ journey }}
            productType="flight"
            offerTotal={Math.abs(overrideTotalCost ?? (fareFamily.costWithInsurance?.all || fareFamily.cost?.all || 0))}
            offerId={journey.id}
          />
          {showLoyaltyPoints && <LoyaltyPointsEarn calculationRequests={calculationRequests} calculationType="exact" />}

          {groupedFeatures.map(feature => (
            <>
              <BodyText variant="small" weight="bold">{feature.title}</BodyText>

              {feature.id !== 'extra' && (
                <VerticalSpacer key={feature.id} gap={8}>
                  {feature.data.map(feature =>
                    <BodyTextBlock
                      variant="small"
                      key={feature.code}
                      startIcon={statusToIcon.get(feature.status)}
                      colour={feature.status === 'NOT_OFFERED' ? 'light-foreground' : undefined}
                    >
                      {formatFeatureDescription(feature)}
                    </BodyTextBlock>,
                  )}
                  {isProtectedFareRulesSmallest(feature, fareFamily) && (
                    <ProtectedFareRuleSpacer />
                  )}
                </VerticalSpacer>
              )}

              {feature.id === 'extra' && (() => {
                const hiddenItemsCount = feature.data.length - MAX_EXTRA_ITEMS
                const shouldShowToggle = hiddenItemsCount >= MIN_HIDDEN_ITEMS_FOR_TOGGLE
                // If only 1 hidden item, show all items without toggle
                const initialItemsToShow = shouldShowToggle ? MAX_EXTRA_ITEMS : feature.data.length

                return (
                  <VerticalSpacer gap={8}>
                    {take(feature.data, initialItemsToShow).map(f =>
                      <BodyTextBlock
                        variant="small"
                        key={f.code}
                        startIcon={statusToIcon.get(f.status)}
                        colour={f.status === 'NOT_OFFERED' ? 'light-foreground' : undefined}
                      >
                        {formatFeatureDescription(f)}
                      </BodyTextBlock>,
                    )}

                    {shouldShowToggle && (
                      <Expander expanded={!!showMore}>
                        <VerticalSpacer gap={8}>
                          {skip(feature.data, MAX_EXTRA_ITEMS).map(f =>
                            <BodyTextBlock
                              variant="small"
                              key={f.code}
                              startIcon={statusToIcon.get(f.status)}
                              colour={f.status === 'NOT_OFFERED' ? 'light-foreground' : undefined}
                            >
                              {formatFeatureDescription(f)}
                            </BodyTextBlock>,
                          )}
                        </VerticalSpacer>
                      </Expander>
                    )}
                  </VerticalSpacer>
                )
              })()}
            </>
          ))}

          {extraFeaturesLength - MAX_EXTRA_ITEMS >= MIN_HIDDEN_ITEMS_FOR_TOGGLE && <TextLink
            onClick={onShowMore}
            endIcon={showMore ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
            size="caption"
          >
            {showMore ? showLessLabel : showMoreLabel}
          </TextLink>}
          {hasNoFlexibilityFareFeatures(features) && <BodyTextBlock
            colour="light-foreground-alt"
            variant="small"
          >
            <FormattedMessage
              id="BwnEZaenmkHhScFCS2u-"
              defaultMessage="View <link>fare rules</link> for changes, cancellation & refunds policy *"
              description="Prompt to view fare rules for changes, cancellation and refunds policy"
              values={{
                link: (chunks: React.ReactNode) => <TextLink size="small" weight="regular" onClick={onViewFareRules}>{chunks}</TextLink>,
              }}
            />
          </BodyTextBlock>}
        </VerticalSpacer>
        {isCroFlexibleFlightFaresEnabled && (
          <>
            <Divider kind="primary" />
            <VerticalSpacer gap={8}>
              <BodyTextBlock variant="small" weight="bold" colour={fareFamily.withEmbeddedInsurance ? 'highlight-primary-dark' : 'light-foreground'}>
                {cancellationProtectionLabel}
              </BodyTextBlock>
              <CaptionTextBlock
                variant="medium"
                startIcon={fareFamily.withEmbeddedInsurance ? <LineShieldCheckIcon colour="highlight-primary-dark" /> : <LineTimesIcon colour="light-foreground" />}
                colour="light-foreground"
              >
                <FormattedMessage
                  id="FrPrevJZ71dYcUizdax0"
                  defaultMessage="100% refund for <link>covered events</link>"
                  description="Cancellation protection refund description with link to covered events"
                  values={{
                    link: (chunks: React.ReactNode) => <TextLink disabled={!fareFamily.withEmbeddedInsurance} weight="regular" onClick={onClickCfmrUnforeseenEvents}>{chunks}</TextLink>,
                  }}
                />
              </CaptionTextBlock>
            </VerticalSpacer>
          </>
        )}

      </VerticalSpacer>
    </FareCard>
  </NestableClickable>
}

export default FareFamilyFareOption
